/**
 * liveSessionReportFormatter — pure, presentational derivations for live-session reports.
 *
 * Lives in `ports/primary/ui/formatters/` (sibling of `ui/types/`), outside `design-system/`, so DS components
 * stay business-agnostic while importing only pure, Storybook-safe helpers. No `@data` / store /
 * service imports. This lifts the legacy `getEngagementLevel` (inlined in `LiveReportCardComponent`)
 * into one tested place, mapped to semantic color tokens (no hex).
 *
 * Formatters hold pure functions only — never UI/prop *type definitions*.
 */

import moment from "moment"

import { type SemanticColorKey } from "../../../../design-system/theme/tokens/colors"
import {
    type UIActivityLevel,
    type UILiveEngagementScore,
    type UILiveSessionReportDetails,
} from "../types/liveSessionReport"

/** Engagement level label + semantic color for a 0–100 score. Null/undefined → the "-" neutral state. */
export function engagementLevel(score: number | null | undefined): {
    label: string
    color: SemanticColorKey
} {
    if (score == null) return { label: "-", color: "text-muted" }
    if (score >= 90) return { label: "Excellent", color: "primary" }
    if (score >= 70) return { label: "Très bien", color: "success" }
    if (score >= 50) return { label: "Bien", color: "warning" }
    return { label: "À améliorer", color: "danger" }
}

/**
 * The three engagement axes in order (Présence / Participation / Productivité). Each axis carries its
 * own label / max / color / details on the report now, so this just forwards them for the list + bar.
 */
export function engagementScores(scores: NonNullable<UILiveSessionReportDetails["scores"]>): UILiveEngagementScore[] {
    return [scores.assiduity, scores.interaction, scores.productivity]
}

/** Total ≥ this (out of 100), with no alerts → "Séance bien engagée". Legacy `GOOD_SESSION_SCORE`. */
const GOOD_SESSION_SCORE = 70

/**
 * Whether to show the "Séance bien engagée" banner: no points d'attention AND a good total. Scores only
 * exist for real, non-trial reports, so the legacy `isEngagementReport` conjunct is implied.
 */
export function showEngagementOk(scores: NonNullable<UILiveSessionReportDetails["scores"]>): boolean {
    return scores.alerts.length === 0 && scores.total >= GOOD_SESSION_SCORE
}

/**
 * A session duration in **seconds** → "1 h 25 min 30 s" (the hours part only above one hour).
 *
 * Literal port of legacy `formatHoursMinutesAndSeconds` (`LiveReportComponent:89-122`), which
 * returned JSX; the decomposition is legacy `hoursMinutesAndSecondsFromSeconds`
 * (`data/utility/Utils:484`) — `moment.duration`, so like the legacy it wraps past 24 h.
 * Not `dateFormatter.formatDuration`: that one takes minutes and renders "1h30".
 */
export function sessionDurationLabel(seconds: number | null | undefined): string {
    const duration = moment.duration(seconds ?? 0, "seconds")
    const hours = duration.hours()
    const parts = hours > 0 ? [`${hours} h`] : []
    parts.push(`${duration.minutes()} min`, `${duration.seconds()} s`)
    return parts.join(" ")
}

/** A chapter's "+N travail/travaux envoyé(s)" counter label (validated work). */
export function validatedWorkLabel(count: number): string {
    return `+${count} ${count > 1 ? "travaux envoyés" : "travail envoyé"}`
}

/** A chapter's "+N travail/travaux à faire" counter label (remaining work). */
export function todoWorkLabel(count: number): string {
    return `+${count} ${count > 1 ? "travaux à faire" : "travail à faire"}`
}

/** Activity intensity level → its badge label (legacy `materialLevelsNames`). */
const ACTIVITY_LEVEL_LABEL: Record<UIActivityLevel, string> = {
    1: "Révision du cours",
    2: "Entraînement",
    3: "Approfondissement",
}
export function activityLevelLabel(level: UIActivityLevel): string {
    return ACTIVITY_LEVEL_LABEL[level]
}

/** Activity intensity level → its badge variant (green / blue / orange, mirroring `materialLevelsColors`). */
export function activityLevelVariant(level: UIActivityLevel): "success" | "info" | "warning" {
    const variants = { 1: "success", 2: "info", 3: "warning" } as const
    return variants[level]
}

/** The mastery bar's flag color: green once all assigned work is sent, accent while work remains to do. */
export function chapterProgressFlagColor(todoCount: number): SemanticColorKey {
    return todoCount > 0 ? "accent" : "success"
}

/** XP needed to fully master a chapter — legacy default when the static config isn't provided. */
const DEFAULT_XP_TO_VALIDATE = 350

/**
 * The chapter mastery bar's display values, derived from raw XP (mirrors the legacy XP bar):
 *  - full-bar scale = max(xpToValidate, xpTotal) (XP to master the chapter)
 *  - `value`  = achieved fill % (xpGained / scale)
 *  - `flagAt` = assigned milestone % (xpTotal / scale)
 *  - `label`  = "gained / total" over the assigned batch (e.g. "75 / 100")
 */
export function chapterMasteryBar(
    xpGained: number,
    xpTotal: number,
    xpToValidate: number = DEFAULT_XP_TO_VALIDATE
): { value: number; flagAt: number; label: string } {
    const scale = Math.max(xpToValidate, xpTotal)
    const pct = (xp: number): number => (scale > 0 ? (xp / scale) * 100 : 0)
    return { value: pct(xpGained), flagAt: pct(xpTotal), label: `${xpGained} / ${xpTotal}` }
}
